import { Check } from "lucide-react";
import { Link } from "react-router";
import type { LinkedBlueprintArtefact, OutOfSyncArtefact } from "./blueprintTypes";
import { OutOfSyncBadge } from "./OutOfSyncBadge";

interface Props {
  artefacts: LinkedBlueprintArtefact[];
  campaignId: string;
  onRegenerate?: (kind: OutOfSyncArtefact["kind"]) => void;
  // When provided, the panel renders a "Regenerate all" header button as
  // soon as more than one artefact is out of sync. Kicks off the regenerate
  // run for every out-of-sync kind in one click.
  onRegenerateAll?: () => void;
  regeneratingAll?: boolean;
}

// Mirrors the staff-audience URLs in `app/services/campaign_artefacts.rb`.
// Story #2252 retired the Define → Guides tab — per-step interview guides
// live on the Selection Process editor; the Assessment Guide lives alongside
// the matrix on Define → Assessment.
function hrefForKind(kind: LinkedBlueprintArtefact["kind"], campaignId: string): string {
  switch (kind) {
    case "screening_criteria":
      return `/campaigns/${campaignId}/define/blueprint#screening-criteria`;
    case "interview_guide":
      return `/campaigns/${campaignId}/define/selection-process`;
    case "assessment_matrix":
      return `/campaigns/${campaignId}/define/assessment`;
    case "candidate_scoring_matrix":
      return `/campaigns/${campaignId}/candidate-scoring-matrix`;
  }
}

export function LinkedArtefactsPanel({
  artefacts,
  campaignId,
  onRegenerate,
  onRegenerateAll,
  regeneratingAll,
}: Props) {
  const outOfSyncCount = artefacts.filter((a) => a.outOfSync).length;
  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-text-secondary">Linked artefacts</h3>
        {onRegenerateAll && outOfSyncCount > 1 && (
          <button
            type="button"
            onClick={onRegenerateAll}
            disabled={regeneratingAll}
            data-testid="regenerate-all-linked-artefacts"
            className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-700 dark:hover:bg-amber-600"
          >
            {regeneratingAll ? "Starting…" : `Regenerate all out-of-sync (${outOfSyncCount})`}
          </button>
        )}
      </div>
      {artefacts.length === 0 ? (
        <p className="text-xs text-text-muted">No linked artefacts yet</p>
      ) : (
        <ul className="space-y-2">
          {artefacts.map((a) => (
            <li
              key={`${a.kind}-${a.id}`}
              className="flex items-center justify-between gap-2 rounded border border-border px-3 py-2 text-xs"
            >
              <div className="flex-1">
                <div className="font-medium text-text-primary">{a.name}</div>
                <div className="text-text-muted">
                  {a.pinnedVersionNumber != null
                    ? `Generated from v${a.pinnedVersionNumber}`
                    : "Not generated yet"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {a.outOfSync ? (
                  <OutOfSyncBadge
                    onRegenerate={onRegenerate ? () => onRegenerate(a.kind) : undefined}
                  />
                ) : a.pinnedVersionNumber != null ? (
                  <span className="inline-flex items-center gap-1 text-text-faint">
                    <Check size={12} /> In sync
                  </span>
                ) : null}
                <Link
                  to={hrefForKind(a.kind, campaignId)}
                  className="rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
                >
                  View
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
